import { useCallback } from 'react';
import { APP_NAME } from '@/shared/config';
import { getDeviceInfo } from '@/shared/utils';
import type { Language } from '@/shared/types';

type VisitorInfo = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  theme?: string;
  language?: string;
  isAuthenticated?: boolean;
};

export function useZohoChat() {
  const getInstance = () => window.$zoho?.salesiq;

  const open = useCallback(() => {
    const zoho = getInstance();

    if (!zoho?.chat) {
      console.warn('[Zoho] chat.start() called but SalesIQ not yet loaded');
      return;
    }

    zoho.chat?.start();

    if (!zoho.floatwindow) {
      console.warn(
        '[Zoho] floatwindow.visible() called but SalesIQ not yet loaded'
      );
      return;
    }

    zoho.floatwindow?.visible('show');
  }, []);

  const reset = useCallback(() => {
    const zoho = getInstance();
    if (!zoho?.reset) {
      console.warn('[Zoho] reset() called but SalesIQ not yet loaded');
      return;
    }
    zoho.reset();
  }, []);

  const setZohoWidgetLanguage = useCallback(
    (language: Language) => {
      const interval = setInterval(() => {
        const zoho = getInstance();
        if (!zoho) return;

        zoho?.language?.(language);

        clearInterval(interval);
      }, 200);
    },
    [reset]
  );

  const setVisitor = useCallback((info: VisitorInfo) => {
    const zoho = getInstance();

    if (!zoho?.visitor) {
      console.warn('[Zoho] setVisitor() called but SalesIQ not yet loaded');
      return;
    }

    const {
      firstName,
      lastName,
      email,
      phone,
      theme,
      language,
      isAuthenticated,
    } = info;

    const { os } = getDeviceInfo();

    if (firstName) zoho.visitor.firstname(firstName);
    if (lastName) zoho.visitor.lastname(lastName);
    if (email) zoho.visitor.email(email);
    if (phone) zoho.visitor.contactnumber(phone);

    zoho.visitor.info({
      'User Agent': `app=${APP_NAME};os=${os};theme=${theme};lang=${language}`,
      'Auth Status': `User ${isAuthenticated ? 'is' : 'is not'} authenticated`,
    });
  }, []);

  return { open, reset, setVisitor, setZohoWidgetLanguage };
}
